import { extractScriptNodeText } from '@/common/utils/scriptNodeText'

export type ListboxCatalogRow = {
  code: string
  image?: string
  text?: string
}

function parseSetFieldCode(raw: string): string | undefined {
  const trimmed = raw.trim().replace(/,\s*$/, '')
  if (!trimmed) return undefined
  if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase()
  const quoted = trimmed.match(/^["'](.*)["']$/)
  if (quoted) return quoted[1]
  return trimmed
}

function parseSetFieldText(raw: string): string | undefined {
  const trimmed = raw.trim().replace(/,\s*$/, '')
  if (!trimmed) return undefined
  const quoted = trimmed.match(/^["'](.*)["']$/)
  if (quoted) return quoted[1]
  return trimmed
}

function parseListboxCatalogBlock(block: string): ListboxCatalogRow | undefined {
  const codeMatch =
    block.match(/setField\s*\(\s*["']code["']\s*,\s*([^)]+?)\s*\)/i) ??
    block.match(/setField\s*\(\s*["']_id["']\s*,\s*([^)]+?)\s*\)/i)
  if (!codeMatch) return undefined
  const code = parseSetFieldCode(codeMatch[1])
  if (code === undefined) return undefined

  const imageMatch = block.match(
    /setField\s*\(\s*["'](?:image|icon)["']\s*,\s*["']([^"']+)["']\s*\)/i,
  )

  const textMatch =
    block.match(/setField\s*\(\s*["']description["']\s*,\s*([^)]+?)\s*\)/i) ??
    block.match(/setField\s*\(\s*["']name["']\s*,\s*([^)]+?)\s*\)/i) ??
    block.match(/setField\s*\(\s*["']fullName["']\s*,\s*([^)]+?)\s*\)/i)
  const text = textMatch ? parseSetFieldText(textMatch[1]) : undefined

  return {
    code,
    image: imageMatch?.[1],
    text: text && text !== '' ? text : undefined,
  }
}

/** Из onLoad/add panel column — table.newRow() / ResultStub().setField("image"|"icon"/"code", …). */
export function parseListboxCatalogFromColumnScript(script: string | undefined): ListboxCatalogRow[] {
  if (!script || typeof script !== 'string') return []

  const rows: ListboxCatalogRow[] = []
  const seen = new Set<string>()

  const blocks = [
    ...script.split(/table\.newRow\s*\(\s*\)/i).slice(1),
    ...script.split(/ResultStub\s*\(\s*\)/i).slice(1),
  ]

  for (const block of blocks) {
    const row = parseListboxCatalogBlock(block)
    if (!row) continue
    const key = `${row.code}::${row.image ?? ''}::${row.text ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    rows.push(row)
  }

  return rows
}

export function parseListboxCatalogFromColumnMeta(column: unknown): ListboxCatalogRow[] {
  if (!column || typeof column !== 'object') return []
  const col = column as Record<string, unknown>
  const fromOnLoad = parseListboxCatalogFromColumnScript(extractScriptNodeText(col.onLoad))
  if (fromOnLoad.length) return fromOnLoad

  const add = col.add
  if (Array.isArray(add)) {
    for (const entry of add) {
      const parsed = parseListboxCatalogFromColumnScript(extractScriptNodeText((entry as any)?.code))
      if (parsed.length) return parsed
    }
  } else if (add && typeof add === 'object') {
    return parseListboxCatalogFromColumnScript(extractScriptNodeText((add as any).code))
  }

  return parseListboxCatalogFromColumnScript(extractScriptNodeText(col.code))
}

function normalizeListboxCode(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return String(value)
  return String(value).trim()
}

export function mapListboxCatalogRowsToSelectOptions(
  catalogRows: ListboxCatalogRow[] | undefined,
): Array<{ value: string; text: string; label: string }> {
  if (!catalogRows?.length) return []

  const options: Array<{ value: string; text: string; label: string }> = []
  const seen = new Set<string>()

  for (const row of catalogRows) {
    const value = normalizeListboxCode(row.code)
    if (seen.has(value)) continue
    seen.add(value)
    const label = row.text?.trim() || value
    options.push({ value, text: label, label })
  }

  return options
}

export function resolveListboxImageFromCatalogRows(
  catalogRows: ListboxCatalogRow[] | undefined,
  cellValue: unknown,
): string | undefined {
  if (!catalogRows?.length) return undefined
  const codeStr = normalizeListboxCode(cellValue)
  if (codeStr === '') return undefined

  for (const row of catalogRows) {
    if (normalizeListboxCode(row.code) !== codeStr) continue
    const image = row.image?.trim()
    if (image) return image
  }

  return undefined
}
